import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Users,
  DollarSign,
  Target,
  Calendar,
  BarChart3,
  RefreshCw,
  GitBranch,
  Info
} from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from '@/contexts/RoleContext';
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useStageMapping } from "@/hooks/useStageMapping";
import { isCategoryAtOrDeeper } from "@/lib/stage-categories";
import { useSetupStatus } from "@/hooks/useSetupStatus";
import { SetupWarning } from "@/components/ui/setup-warning";
import { PageTour } from "@/components/tour/PageTour";
import { marketingSteps } from "@/lib/tour-steps";

interface CampaignMetric {
  id: string;
  platform: string;
  campaign_name: string;
  campaign_id: string;
  date: string;
  impressions: number;
  clicks: number;
  conversions: number;
  spend_cents: number;
  ctr: number;
  cpc_cents: number;
}

export default function Marketing() {
  const { clientId } = useRole();
  const [metrics, setMetrics] = useState<CampaignMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7' | '30' | '90'>('7');
  const [lastSync, setLastSync] = useState<Date | null>(null);

  useEffect(() => {
    fetchMarketingData();
  }, [dateRange, clientId]);

  const fetchMarketingData = async () => {
    setLoading(true);
    try {
      if (!clientId) {
        setLoading(false);
        return;
      }

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const { data, error } = await supabase
        .from('campaign_metrics')
        .select('*')
        .eq('client_id', clientId)
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (error) throw error;
      setMetrics(data || []);
      setLastSync(new Date());
    } catch (error: unknown) {
      console.error('Error fetching marketing data:', error);
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate aggregated metrics
  const totalLeads = metrics.reduce((sum, m) => sum + (m.conversions || 0), 0);
  const totalSpend = metrics.reduce((sum, m) => sum + (m.spend_cents || 0), 0) / 100;
  const totalClicks = metrics.reduce((sum, m) => sum + (m.clicks || 0), 0);
  const totalImpressions = metrics.reduce((sum, m) => sum + (m.impressions || 0), 0);

  const costPerLead = totalLeads > 0 ? totalSpend / totalLeads : 0;
  const avgCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  // Get booked demos count from Supabase demos table
  const [bookedDemos, setBookedDemos] = useState(0);
  useEffect(() => {
    if (!clientId) return;

    const fetchBookedDemos = async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(String(dateRange)));

      const { count, error } = await supabase
        .from('demos')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', clientId)
        .gte('demo_date', startDate.toISOString().split('T')[0]);

      if (!error) setBookedDemos(count || 0);
    };
    fetchBookedDemos();
  }, [dateRange, clientId]);

  // Pipeline demos from funnel_events via stage mappings
  const { mapStage, mappings } = useStageMapping(clientId);
  const hasMappings = mappings.length > 0;
  const setupStatus = useSetupStatus(clientId);

  const [funnelEvents, setFunnelEvents] = useState<{ stage: string }[]>([]);
  useEffect(() => {
    if (!clientId) return;

    const fetchFunnelEvents = async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const { data, error } = await supabase
        .from('funnel_events')
        .select('stage')
        .eq('client_id', clientId)
        .gte('lead_created_at', startDate.toISOString());

      if (!error) setFunnelEvents(data || []);
    };
    fetchFunnelEvents();
  }, [clientId, dateRange]);

  const pipelineDemos = useMemo(() => {
    if (!hasMappings) return 0;
    return funnelEvents.filter(e =>
      isCategoryAtOrDeeper(mapStage(e.stage), 'demo_booked')
    ).length;
  }, [funnelEvents, hasMappings, mapStage]);

  const costPerDemo = bookedDemos > 0 ? totalSpend / bookedDemos : 0;
  const costPerScheduled = totalLeads > 0 ? totalSpend / totalLeads : 0;

  // Group metrics by date for chart
  const dailyData = metrics.reduce((acc: any[], m) => {
    const existing = acc.find(d => d.date === m.date);
    if (existing) {
      existing.leads += m.conversions || 0;
      existing.spend += (m.spend_cents || 0) / 100;
      existing.clicks += m.clicks || 0;
    } else {
      acc.push({
        date: m.date,
        leads: m.conversions || 0,
        spend: (m.spend_cents || 0) / 100,
        clicks: m.clicks || 0
      });
    }
    return acc;
  }, []).sort((a, b) => a.date.localeCompare(b.date));

  // Group by campaign
  const campaignData = metrics.reduce((acc: any[], m) => {
    const existing = acc.find(c => c.campaign_id === m.campaign_id);
    if (existing) {
      existing.leads += m.conversions || 0;
      existing.spend += (m.spend_cents || 0) / 100;
      existing.clicks += m.clicks || 0;
    } else {
      acc.push({
        campaign_id: m.campaign_id,
        campaign_name: m.campaign_name,
        platform: m.platform,
        leads: m.conversions || 0,
        spend: (m.spend_cents || 0) / 100,
        clicks: m.clicks || 0
      });
    }
    return acc;
  }, []);

  const formatCurrency = (value: number) => 
    `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <>
    <PageTour steps={marketingSteps} tourId="marketing" />
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            Marketing Performance
            <TooltipProvider delayDuration={0}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Campaign data syncs daily from connected ad accounts (Google Ads and/or Meta Ads). Connect at least one ad platform to see metrics here.</p>
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          </h1>
          <p className="text-muted-foreground">Live campaign tracking from Meta & Google Ads</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as '7' | '30' | '90')}
            className="px-4 py-2 border border-border rounded-lg bg-surface text-sm"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchMarketingData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Sync
          </Button>
        </div>
      </div>

      {/* Last Sync */}
      {lastSync && (
        <p className="text-xs text-muted-foreground">
          Last synced: {lastSync.toLocaleTimeString()}
        </p>
      )}

      {/* No integrations message */}
      {!loading && metrics.length === 0 && (
        <div className="space-y-2">
          {!setupStatus.googleAdsConnected && !setupStatus.metaAdsConnected && !(setupStatus.googleAdsNotNeeded && setupStatus.metaAdsNotNeeded) && (
            <SetupWarning message="No ad accounts connected — connect Meta Ads or Google Ads to see campaign performance." />
          )}
          {(setupStatus.googleAdsConnected || setupStatus.metaAdsConnected) && !setupStatus.stageMappingsConfigured && (
            <SetupWarning message="Pipeline stages are being configured — demo metrics from campaigns will appear once mapping is complete." />
          )}
        </div>
      )}

      {/* Key Metrics */}
      <div data-tour-id="key-metrics" className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${hasMappings ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
        <VentureCard>
          <VentureCardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <Users className="w-5 h-5 text-secondary" />
            </div>
            <p className="text-3xl font-bold">{totalLeads}</p>
            <div className="flex items-center gap-1">
              <p className="text-sm text-muted-foreground">Total Leads</p>
              <TooltipProvider><UITooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent><p>Number of leads generated from your ad campaigns</p></TooltipContent></UITooltip></TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold">{formatCurrency(costPerLead)}</p>
            <p className="text-sm text-muted-foreground">Cost per Lead</p>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold">{bookedDemos}</p>
            <p className="text-sm text-muted-foreground">Booked Demos</p>
          </VentureCardContent>
        </VentureCard>

        {hasMappings && (
          <VentureCard>
            <VentureCardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <GitBranch className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-3xl font-bold">{pipelineDemos}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Pipeline Demos</p>
                <TooltipProvider><UITooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent><p>Demos generated from your ad campaigns, tracked through GHL</p></TooltipContent></UITooltip></TooltipProvider>
              </div>
            </VentureCardContent>
          </VentureCard>
        )}

        <VentureCard>
          <VentureCardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <Calendar className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold">{formatCurrency(costPerDemo)}</p>
            <p className="text-sm text-muted-foreground">Cost per Booked Demo</p>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <p className="text-xl font-bold">{formatCurrency(totalSpend)}</p>
            <p className="text-xs text-muted-foreground">
              Total Ad Spend
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total ad spend across connected platforms</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </p>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <p className="text-xl font-bold">{formatCurrency(costPerScheduled)}</p>
            <p className="text-xs text-muted-foreground">
              Cost per Scheduled
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Cost Per Click — average amount spent per ad click</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </p>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <p className="text-xl font-bold">{totalClicks.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">
              Total Clicks
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Number of clicks on your ads across all campaigns</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </p>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <p className="text-xl font-bold">{avgCTR.toFixed(2)}%</p>
            <p className="text-xs text-muted-foreground">
              Avg CTR
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click-through rate — clicks divided by impressions</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </p>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Leads Over Time Chart */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-secondary" />
            Leads Over Time
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="date" 
                  stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--surface))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Line type="monotone" dataKey="leads" stroke="hsl(var(--secondary))" strokeWidth={2} dot={{ fill: 'hsl(var(--secondary))' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No data available for this period</p>
              <p className="text-sm mt-2">Connect your Meta or Google Ads account to see live data</p>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Campaign Performance */}
      <VentureCard data-tour-id="campaign-table">
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-secondary" />
            Campaign Performance
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {campaignData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Campaign</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Platform</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Leads</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Clicks</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Spend</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">CPL</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignData.map((campaign) => (
                    <tr key={campaign.campaign_id} className="border-b border-border hover:bg-surface">
                      <td className="py-3 px-4">
                        <p className="font-medium">{campaign.campaign_name}</p>
                        <p className="text-xs text-muted-foreground">ID: {campaign.campaign_id}</p>
                      </td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={campaign.platform === 'meta_ads' ? 'info' : 'warning'}>
                          {campaign.platform === 'meta_ads' ? 'Meta Ads' : 'Google Ads'}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4 text-right font-medium">{campaign.leads}</td>
                      <td className="py-3 px-4 text-right">{campaign.clicks.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(campaign.spend)}</td>
                      <td className="py-3 px-4 text-right font-medium">
                        {campaign.leads > 0 ? formatCurrency(campaign.spend / campaign.leads) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No campaigns found</p>
              <p className="text-sm mt-2">Campaign data will appear once your ads accounts are connected</p>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </motion.div>
    </>
  );
}
